import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sunrise, Sparkles, Timer, ArrowRight, RefreshCcw, Loader2,
  CheckCircle2, Compass, MapPin, Award, TrendingUp, Settings as Cog,
  Users, HandshakeIcon, Eye, Calendar, Brain,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const BENEFIT_ICON = {
  reseau: Users, partenariat: HandshakeIcon, rendez_vous: Calendar,
  reference: ArrowRight, visibilite: Eye, autre: Sparkles,
};

const BENEFIT_LABEL = {
  reseau: "Développement de réseau",
  partenariat: "Partenariat",
  rendez_vous: "Rendez-vous client",
  reference: "Référence potentielle",
  visibilite: "Visibilité",
  autre: "Bénéfice",
};

function ActionCard({ action, i, onDone, onPostpone, onOpen, onPrepare, onNotRelevant }) {
  const BenIcon = BENEFIT_ICON[action.expected_benefit_kind] || Sparkles;
  const impact = action.impact || {};
  const justifs = action.why_prioritized_fr || impact.justifications_fr || [];
  return (
    <div
      className="bg-white border rounded-xl p-5 hover:shadow-lg transition"
      style={{ borderColor: "#E5E7EB" }}
      data-testid={`abi-dashboard-action-${i}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-serif font-bold text-lg"
              style={{ background: "#0B2545", color: "#C5A880" }}>
          {i + 1}
        </div>
        <div className="flex-1 min-w-0">
          <button type="button" onClick={() => onOpen?.(action)}
                  className="text-left w-full">
            <div className="flex items-baseline gap-2">
              <h3 className="font-serif text-lg font-semibold text-slate-900 leading-tight flex-1"
                  style={{ color: "#0B2545" }}>{action.title_fr}</h3>
              {(action.impact_score || action.priority_score) && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded"
                       style={{ background: "#0B2545", color: "#C5A880" }}>
                  Impact {action.impact_score || action.priority_score}
                </span>
              )}
            </div>
          </button>

          {/* Copilote justifications — 3-4 bullets */}
          {justifs.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-sm">
              {justifs.slice(0, 4).map((j, k) => (
                <li key={k} className="text-slate-700 pl-2 border-l-2"
                    style={{ borderColor: "#C5A880" }}>
                  {j}
                </li>
              ))}
            </ul>
          )}

          {/* Qualitative buckets — human-friendly, no fake percentages */}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
            {action.impact_bucket_fr && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                    style={{ background: "#F0FDF4", color: "#166534" }}>
                Impact {action.impact_bucket_fr}
              </span>
            )}
            {action.urgence_bucket_fr && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                    style={{ background: "#FEF3C7", color: "#78350F" }}>
                Urgence {action.urgence_bucket_fr}
              </span>
            )}
            {action.effort_bucket_fr && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                Effort {action.effort_bucket_fr}
              </span>
            )}
          </div>

          {/* WHY + ACTION */}
          <p className="text-sm text-slate-700 mt-3">
            <span className="uppercase tracking-wide text-[10px] font-bold"
                  style={{ color: "#7A5B14" }}>Pourquoi</span>{" · "}
            {action.why_relevant_short_fr}
          </p>
          <p className="text-sm text-slate-800 mt-1 font-medium">
            <span className="uppercase tracking-wide text-[10px] font-bold"
                  style={{ color: "#7A5B14" }}>Action</span>{" · "}
            {action.recommended_action_fr}
          </p>
          {action.recommended_channel_fr && (
            <p className="text-sm text-slate-700 mt-1">
              <span className="uppercase tracking-wide text-[10px] font-bold"
                    style={{ color: "#7A5B14" }}>Canal</span>{" · "}
              {action.recommended_channel_fr}
            </p>
          )}
          {action.next_step_fr && (
            <p className="text-sm text-slate-800 mt-1 font-medium">
              <span className="uppercase tracking-wide text-[10px] font-bold"
                    style={{ color: "#7A5B14" }}>Prochaine étape</span>{" · "}
              {action.next_step_fr}
            </p>
          )}

          {/* Expected outcome + risk */}
          {action.expected_outcome_fr && (
            <p className="text-sm text-emerald-800 mt-2">
              <span className="uppercase tracking-wide text-[10px] font-bold">Résultat attendu</span>{" · "}
              {action.expected_outcome_fr}
            </p>
          )}
          {action.risk_if_postponed_fr && (
            <p className="text-sm mt-1"
                style={{ color: "#B91C1C" }}>
              <span className="uppercase tracking-wide text-[10px] font-bold">Risque si reporté</span>{" · "}
              {action.risk_if_postponed_fr}
            </p>
          )}

          {/* TIME + BENEFIT + territory */}
          <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700">
              <Timer size={12} /> {action.estimated_time_minutes} min
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
                  style={{ background: "#FBF6EA", color: "#7A5B14" }}>
              <BenIcon size={12} /> {BENEFIT_LABEL[action.expected_benefit_kind] || "Bénéfice"}
            </span>
            {action.territory?.city && (
              <span className="inline-flex items-center gap-1 text-slate-500">
                <MapPin size={11} /> {action.territory.city}
              </span>
            )}
          </div>

          {/* Doctrine ABI · Enveloppe explicative à 5 champs */}
          {action.explanation && (
            <div className="mt-4 rounded-lg p-3 border"
                  style={{ borderColor: "#C5A880", background: "#FBF6EA" }}
                  data-testid={`abi-dashboard-action-${i}-explanation`}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-2"
                    style={{ color: "#7A5B14" }}>
                ★ Pourquoi ABI recommande cette action
              </div>
              <div className="space-y-2 text-[13px] leading-relaxed">
                <div>
                  <span className="font-bold" style={{ color: "#0B2545" }}>① Pourquoi · </span>
                  <span className="text-slate-800">{action.explanation.why_fr}</span>
                </div>
                {(action.explanation.evidence_fr || []).length > 0 && (
                  <div>
                    <span className="font-bold" style={{ color: "#0B2545" }}>② Données · </span>
                    <ul className="ml-4 mt-0.5 text-slate-700 list-disc">
                      {action.explanation.evidence_fr.slice(0, 4).map((e, k) => (
                        <li key={k}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <span className="font-bold" style={{ color: "#0B2545" }}>③ Impact attendu · </span>
                  <span className="text-slate-800">{action.explanation.expected_impact_fr}</span>
                </div>
                <div>
                  <span className="font-bold" style={{ color: "#0B7A3B" }}>④ Ce que font les meilleurs · </span>
                  <span className="text-slate-800">{action.explanation.top_performers_do_fr}</span>
                </div>
                <div>
                  <span className="font-bold" style={{ color: "#B91C1C" }}>⑤ Risque si non exécutée · </span>
                  <span className="text-slate-800">{action.explanation.risk_if_ignored_fr}</span>
                </div>
              </div>
            </div>
          )}

          {/* 6 CTA buttons — human validation always */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onDone?.(action)}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold text-white hover:opacity-90"
              style={{ background: "#0B2545" }}
              data-testid={`abi-dashboard-action-${i}-done`}
            >
              <CheckCircle2 size={12} className="inline mr-1" /> Faire maintenant
            </button>
            <button
              type="button"
              onClick={() => onPrepare?.(action)}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold hover:opacity-90"
              style={{ background: "#C5A880", color: "#0B2545" }}
              data-testid={`abi-dashboard-action-${i}-prepare`}
            >
              <Sparkles size={12} className="inline mr-1" /> Préparer
            </button>
            <button
              type="button"
              onClick={() => onPostpone?.(action)}
              className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50"
              style={{ borderColor: "#E5E7EB", color: "#5B5B62" }}
              data-testid={`abi-dashboard-action-${i}-postpone`}
            >
              Reporter
            </button>
            <button
              type="button"
              onClick={() => onNotRelevant?.(action)}
              className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50"
              style={{ borderColor: "#E5E7EB", color: "#5B5B62" }}
              data-testid={`abi-dashboard-action-${i}-not-relevant`}
            >
              Non pertinent
            </button>
            <Link
              to="/app/abi/radar"
              className="text-xs text-slate-500 hover:text-slate-900 ml-auto inline-flex items-center gap-1"
            >
              Détails <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneCls = tone === "green" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : tone === "gold"  ? "border-amber-200"
                : "border-slate-200";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}
         style={tone === "gold" ? { background: "#FBF6EA", color: "#7A5B14" } : {}}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-serif text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

export default function AbiDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);         // Prepare modal state
  const [draftLoading, setDraftLoading] = useState(false);
  const [learning, setLearning] = useState(null);   // Monthly learning recap

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abi/copilot/morning");
      const answers = r.data?.answers || {};
      setData({
        settings:              r.data?.settings || {},
        strategic_message_fr:  r.data?.strategic_message_fr,
        main_blocker:          r.data?.main_blocker,
        main_lever:            r.data?.main_lever,
        trajectory:            r.data?.trajectory,
        trajectory_progress:   r.data?.trajectory_progress,
        week_stats:            _weekStatsFromFindings(r.data?.findings_all || [],
                                                        r.data?.signal_summary || {}),
        stage_counts:          r.data?.radar_context?.stage_counts || {},
        priority_actions:      (answers.q2_top_actions || []).map((a, i) => ({
          ...a,
          why_prioritized_fr:   (answers.q3_why_prioritized?.[i]?.justifications_fr || []),
          expected_outcome_fr:   answers.q4_expected_outcome?.[i]?.expected_outcome_fr,
          risk_if_postponed_fr:  answers.q5_risks_if_postponed?.[i]?.risk_if_postponed_fr,
          explanation:           a.explanation,   // ★ Doctrine 5-field envelope
        })),
        top_opportunities:  [],
        blockers:           answers.q1_blockers || [],
        findings_all:       r.data?.findings_all || [],
      });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Accès réservé aux professionnels ALTIORA.");
      } else {
        toast.error("Impossible de charger le tableau de bord.");
      }
    } finally { setLoading(false); }
  };

  function _weekStatsFromFindings(findings, sig) {
    // Best-effort derivation from signals; keeps old shape backward-compatible
    return {
      new:          sig.contacts_attempted || 0,
      contacted:    sig.meetings_scheduled || 0,
      appointments: sig.meetings_completed || 0,
      converted:    sig.conversion_closed || 0,
    };
  }

  useEffect(() => { load(); }, []);

  // Fetch the (cached) monthly learning recap in parallel — never blocks the dashboard
  useEffect(() => {
    api.get("/abi/copilot/learning/monthly-recap")
      .then(r => setLearning(r.data))
      .catch(() => {});
  }, []);

  // Fetch the professional memory summary — la mémoire d'ABI
  const [memory, setMemory] = useState(null);
  useEffect(() => {
    api.get("/abi/copilot/memory/summary")
      .then(r => setMemory(r.data))
      .catch(() => {});
  }, []);

  // Compliance alerts — doctrine v5
  const [compliance, setCompliance] = useState(null);
  useEffect(() => {
    api.get("/abi/copilot/compliance/alerts")
      .then(r => setCompliance(r.data))
      .catch(() => {});
  }, []);

  const markDone = async (action) => {
    try {
      await api.post(`/abi/radar/opportunities/${action.opportunity_id}/pipeline`,
                      { pipeline_stage: "contact_fait" });
      // Emit feedback signal so the copilot learns
      await api.post("/abi/copilot/feedback/action", {
        opportunity_id: action.opportunity_id,
        outcome: "contact_etabli",
        contact_reached: true,
        actual_minutes: action.estimated_time_minutes,
      });
      toast.success("Marquée comme faite — pipeline avancé + apprentissage.");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const postpone = async (action) => {
    try {
      await api.post(`/abi/radar/opportunities/${action.opportunity_id}/pipeline`,
                      { pipeline_stage: "a_analyser" });
      await api.post("/abi/copilot/feedback/skipped", {
        opportunity_id: action.opportunity_id,
        reason: "report_volontaire",
      });
      toast.info("Reportée à demain.");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const markNotRelevant = async (action) => {
    try {
      await api.post(`/abi/radar/opportunities/${action.opportunity_id}/pipeline`,
                      { pipeline_stage: "abandonnee" });
      await api.post("/abi/copilot/feedback/skipped", {
        opportunity_id: action.opportunity_id,
        reason: "mauvaise_priorite",
      });
      toast.info("Marquée comme non pertinente. Le Copilote en tiendra compte.");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const prepare = async (action) => {
    setDraft({ action, kind: "email", text: null });
    setDraftLoading(true);
    try {
      const r = await api.post("/abi/copilot/prepare", {
        opportunity_id: action.opportunity_id,
        kind: "email",
      });
      setDraft({ action, kind: "email", text: r.data?.draft_fr, disclaimer: r.data?.disclaimer_fr });
    } catch (e) {
      toast.error("Impossible de générer le brouillon.");
      setDraft(null);
    } finally { setDraftLoading(false); }
  };

  const today = new Date().toLocaleDateString("fr-CA", {
    weekday: "long", day: "numeric", month: "long",
  });

  const settings = data?.settings || {};
  const stats = data?.week_stats || {};
  const actions = data?.priority_actions || [];
  const topOpps = data?.top_opportunities || [];

  return (
    <div data-testid="abi-dashboard-root">
      <TopBar
        title="Aujourd'hui"
        subtitle="Votre plan d'action commercial préparé par ALTIORA."
      />

      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1200px] mx-auto space-y-8">

        {/* Header */}
        <div className="rounded-2xl p-6 md:p-8"
              style={{ background: "linear-gradient(135deg,#0B2545 0%,#061630 100%)", color:"#F5F0E6" }}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
                <Sunrise size={14} />
                <span>{today}</span>
              </div>
              <h1 className="font-serif text-3xl md:text-4xl font-bold mt-1">
                Voici votre journée.
              </h1>
              <p className="text-sm md:text-base opacity-80 mt-1">
                <MapPin size={13} className="inline mr-1" />
                {[settings.city, settings.province].filter(Boolean).join(", ") || "Territoire non défini"} —
                {" "}
                <b>CSF débutant</b>
                {settings.city ? "" : (
                  <>
                    {" · "}
                    <Link to="/app/abi/settings" className="underline">définir mon territoire</Link>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-white/10"
                style={{ borderColor: "#C5A880", color: "#C5A880" }}
                data-testid="abi-dashboard-refresh"
              >
                {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                Actualiser
              </button>
              <Link
                to="/app/abi/settings"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-white/10"
                style={{ borderColor: "#C5A880", color: "#C5A880" }}
                data-testid="abi-dashboard-settings-link"
              >
                <Cog size={12} /> Paramètres
              </Link>
            </div>
          </div>
        </div>

        {/* Strategic morning message — "un directeur des ventes a préparé votre journée" */}
        {data?.strategic_message_fr && (
          <div className="rounded-xl p-5 border-l-4 bg-white"
                style={{ borderLeftColor: "#C5A880", borderColor: "#E5E7EB" }}
                data-testid="abi-dashboard-strategic-message">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2"
                  style={{ color: "#7A5B14" }}>
              Briefing stratégique du matin
            </div>
            <p className="text-slate-800 leading-relaxed">
              {data.strategic_message_fr}
            </p>
            {(data.main_blocker || data.main_lever) && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {data.main_blocker && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
                         style={{ background: "#FEE2E2", color: "#7F1D1D" }}>
                    Frein principal · {data.main_blocker.title_fr}
                  </span>
                )}
                {data.main_lever && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">
                    Levier principal · {data.main_lever.title_fr}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Trajectory progress widget */}
        {data?.trajectory_progress && (
          <div className="bg-white rounded-xl border p-4"
                style={{ borderColor: "#E5E7EB" }}
                data-testid="abi-dashboard-trajectory-progress">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                  Ma trajectoire de réussite
                </div>
                {data.trajectory_progress.north_star ? (
                  <div className="font-serif text-lg" style={{ color: "#0B2545" }}>
                    ⭐ {data.trajectory_progress.north_star.label_fr} —
                    <span className="font-normal text-slate-600 text-sm ml-2">
                      {data.trajectory_progress.north_star.current_value || 0}
                      {" / "}
                      {data.trajectory_progress.north_star.target_value || 0}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 mt-0.5">
                    {data.trajectory_progress.declared_count > 0
                      ? `${data.trajectory_progress.declared_count} objectifs déclarés · ${data.trajectory_progress.completion_pct}% de progression moyenne`
                      : "Aucun objectif déclaré pour le moment."}
                  </div>
                )}
              </div>
              <Link
                to="/app/abi/trajectory"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: "#C5A880", color: "#0B2545" }}
                data-testid="abi-dashboard-trajectory-link"
              >
                {data.trajectory_progress.declared_count > 0 ? "Ajuster ma trajectoire" : "Définir ma trajectoire →"}
              </Link>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div className="h-full rounded-full transition-all"
                    style={{ width: `${data.trajectory_progress.completion_pct || 0}%`, background: "#C5A880" }} />
            </div>
          </div>
        )}

        {/* Learning Engine widget — profession-agnostic monthly recap */}
        {learning && (
          <div className="bg-white rounded-xl border p-4"
                style={{ borderColor: "#E5E7EB" }}
                data-testid="abi-dashboard-learning-widget">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: "#EEF4FF", color: "#0B2545" }}>
                  <Brain size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                    Moteur d'apprentissage · {learning.profession_label_fr}
                  </div>
                  <div className="font-serif text-lg" style={{ color: "#0B2545" }}>
                    Ce que j'ai appris ce mois-ci
                  </div>
                </div>
              </div>
              <Link to="/app/abi/learning"
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
                    style={{ background: "#0B2545", color: "#fff" }}
                    data-testid="abi-dashboard-learning-link">
                Voir le récap →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-2">
              {(learning.q1_contributed_fr || []).slice(0, 1).map((s, i) => (
                <div key={`c-${i}`} className="text-[13px] p-2 rounded"
                      style={{ background: "#DCFCE7", color: "#065F46" }}
                      data-testid="abi-dashboard-learning-preview-contributed">
                  ✓ {s}
                </div>
              ))}
              {(learning.q2_wasted_fr || []).slice(0, 1).map((s, i) => (
                <div key={`w-${i}`} className="text-[13px] p-2 rounded"
                      style={{ background: "#FEE2E2", color: "#991B1B" }}
                      data-testid="abi-dashboard-learning-preview-wasted">
                  ⚠ {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Alerts widget — doctrine v5 · le gardien réglementaire */}
        {compliance && compliance.alerts && compliance.alerts.length > 0 && (
          <div className="rounded-xl border p-4"
                style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }}
                data-testid="abi-dashboard-compliance">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: "#FEE2E2", color: "#B91C1C" }}>
                ⚠
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#B91C1C" }}>
                  Vérifications réglementaires · {compliance.window_days} derniers jours
                </div>
                <div className="font-serif text-lg" style={{ color: "#7F1D1D" }}>
                  {compliance.alerts.length} action(s) à vérifier
                </div>
              </div>
            </div>
            <div className="space-y-2 mt-2">
              {compliance.alerts.map((a, i) => (
                <div key={a.rule_id} data-testid={`abi-dashboard-compliance-${i}`}
                      className="p-2 rounded bg-white text-[13px]"
                      style={{ borderLeft: `4px solid ${a.severity === "high" ? "#B91C1C" : "#B45309"}` }}>
                  <div className="text-slate-900">{a.alert_fr}</div>
                  <div className="text-[11px] text-slate-500 mt-1 italic">
                    📖 {a.card_title_fr} · Preuve {a.evidence_level} · {a.occurrences} occurrence(s)
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Professional Memory widget — l'expérience accumulée d'ABI */}
        {memory && memory.total > 0 && (
          <div className="bg-white rounded-xl border p-4"
                style={{ borderColor: "#E5E7EB" }}
                data-testid="abi-dashboard-memory-widget">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: "#FBF6EA", color: "#7A5B14" }}>
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7A5B14" }}>
                    Mémoire professionnelle · {memory.total} leçon{memory.total > 1 ? "s" : ""} accumulée{memory.total > 1 ? "s" : ""}
                  </div>
                  <div className="font-serif text-lg" style={{ color: "#0B2545" }}>
                    Ce qu'ABI a appris de vous
                  </div>
                </div>
              </div>
              <Link to="/app/abi/learning"
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
                    style={{ background: "#0B2545", color: "#fff" }}
                    data-testid="abi-dashboard-memory-link">
                Consulter →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-2 mt-2">
              {(memory.top_habits_won || []).slice(0, 1).map((m, i) => (
                <div key={`won-${i}`} className="text-[12px] p-2 rounded"
                      style={{ background: "#DCFCE7", color: "#065F46" }}
                      data-testid="abi-dashboard-memory-preview-won">
                  <div className="uppercase text-[9px] tracking-widest font-bold mb-0.5">Habitude gagnante</div>
                  {m.text_fr}
                  {m.times_reinforced > 1 && (
                    <div className="mt-1 text-[10px] opacity-80">× confirmée {m.times_reinforced} mois</div>
                  )}
                </div>
              ))}
              {(memory.top_habits_failed || []).slice(0, 1).map((m, i) => (
                <div key={`fail-${i}`} className="text-[12px] p-2 rounded"
                      style={{ background: "#FEE2E2", color: "#991B1B" }}
                      data-testid="abi-dashboard-memory-preview-failed">
                  <div className="uppercase text-[9px] tracking-widest font-bold mb-0.5">Erreur récurrente</div>
                  {m.text_fr}
                </div>
              ))}
              {(memory.top_skill_gaps || []).slice(0, 1).map((m, i) => (
                <div key={`gap-${i}`} className="text-[12px] p-2 rounded"
                      style={{ background: "#EEF4FF", color: "#0B2545" }}
                      data-testid="abi-dashboard-memory-preview-gap">
                  <div className="uppercase text-[9px] tracking-widest font-bold mb-0.5">Compétence à travailler</div>
                  {m.text_fr}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3 priority actions — the heart of the dashboard */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-serif text-2xl font-bold" style={{ color: "#0B2545" }}>
                🎯 Aujourd'hui, les actions qui vous rapprochent de votre trajectoire
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Si vous ne pouviez faire que 3 choses aujourd'hui, ce seraient celles-ci.
              </p>
            </div>
            <Link
              to="/app/abi/actions"
              className="text-sm font-medium inline-flex items-center gap-1"
              style={{ color: "#7A5B14" }}
              data-testid="abi-dashboard-see-all-actions"
            >
              Voir toutes les actions <ArrowRight size={14} />
            </Link>
          </div>

          {loading && !data ? (
            <div className="p-8 flex items-center gap-2 text-slate-600">
              <Loader2 size={16} className="animate-spin" /> Chargement…
            </div>
          ) : actions.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
              <p className="text-slate-600 mb-3">
                Aucune action prioritaire — toutes vos opportunités ont avancé dans le pipeline.
              </p>
              <Link
                to="/app/abi/radar"
                className="inline-flex items-center gap-1 text-sm font-semibold"
                style={{ color: "#0B2545" }}
              >
                Explorer le Radar <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {actions.map((a, i) => (
                <ActionCard
                  key={a.opportunity_id}
                  action={a}
                  i={i}
                  onDone={markDone}
                  onPostpone={postpone}
                  onPrepare={prepare}
                  onNotRelevant={markNotRelevant}
                />
              ))}
            </div>
          )}
        </section>

        {/* Blockers — the Copilote's diagnosis */}
        {data?.blockers && data.blockers.length > 0 && (
          <section data-testid="abi-dashboard-blockers">
            <h2 className="font-serif text-2xl font-bold mb-4"
                style={{ color: "#B91C1C" }}>
              🔴 Ce qui freine votre progression
            </h2>
            <div className="space-y-3">
              {data.blockers.map((b, i) => (
                <div key={b.pattern_id}
                     className="bg-white border rounded-xl p-4"
                     style={{ borderColor: "#FCA5A5", borderLeftWidth: 4, borderLeftColor: "#B91C1C" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif font-semibold text-slate-900" style={{ color: "#0B2545" }}>
                        {b.title_fr}
                      </h3>
                      <p className="text-sm text-slate-700 mt-1">{b.explanation_fr}</p>
                      <p className="text-sm mt-2" style={{ color: "#0B2545" }}>
                        <b>Action recommandée · </b>{b.recommended_action_fr}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>Confiance : {b.confidence_pct}%</span>
                        <span>· Impact estimé : {b.impact_score}/100</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Week stats */}
        <section>
          <h2 className="font-serif text-xl font-bold mb-4" style={{ color: "#0B2545" }}>
            📊 Cette semaine
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Nouvelles"    value={stats.new || 0}          tone="gold" />
            <StatCard label="Contactées"   value={stats.contacted || 0}    tone="default" />
            <StatCard label="Rendez-vous"  value={stats.appointments || 0} tone="green" />
            <StatCard label="Converties"   value={stats.converted || 0}    tone="green" />
          </div>
        </section>

        {/* Top opportunities from Radar */}
        {topOpps.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-bold" style={{ color: "#0B2545" }}>
                🌟 Top opportunités autour de vous
              </h2>
              <Link
                to="/app/abi/radar"
                className="text-sm font-medium inline-flex items-center gap-1"
                style={{ color: "#7A5B14" }}
                data-testid="abi-dashboard-see-radar"
              >
                Voir le Radar <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {topOpps.slice(0, 3).map((op) => (
                <Link
                  key={op.opportunity_id}
                  to="/app/abi/radar"
                  className="bg-white border rounded-xl p-4 hover:shadow-md transition"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">
                    {op.opportunity_type}
                  </div>
                  <div className="font-serif font-semibold text-slate-900 mb-2"
                        style={{ color: "#0B2545" }}>{op.title_fr}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <MapPin size={11} />
                    {[op.territory?.city, op.territory?.province].filter(Boolean).join(", ")}
                    <span className="ml-auto font-bold" style={{ color: "#7A5B14" }}>
                      {op.priority_score}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Pipeline snapshot */}
        {data?.stage_counts && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-bold" style={{ color: "#0B2545" }}>
                📋 Mon pipeline
              </h2>
              <Link
                to="/app/abi/pipeline"
                className="text-sm font-medium inline-flex items-center gap-1"
                style={{ color: "#7A5B14" }}
                data-testid="abi-dashboard-see-pipeline"
              >
                Voir le pipeline <ArrowRight size={14} />
              </Link>
            </div>
            <div className="bg-white border rounded-xl p-4"
                  style={{ borderColor: "#E5E7EB" }}>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-xs text-center">
                {[
                  ["reperee", "Repérée"],
                  ["a_analyser", "À analyser"],
                  ["a_contacter", "À contacter"],
                  ["contact_fait", "Contact fait"],
                  ["rdv_obtenu", "Rendez-vous"],
                  ["en_suivi", "En suivi"],
                  ["convertie", "Convertie"],
                  ["abandonnee", "Abandonnée"],
                ].map(([k, l]) => (
                  <div key={k} className="p-2 rounded-lg" style={{ background: "#F8FAFC" }}>
                    <div className="font-serif font-bold text-2xl" style={{ color: "#0B2545" }}>
                      {data.stage_counts[k] || 0}
                    </div>
                    <div className="text-[10px] opacity-70">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </div>

      {/* Prepare draft modal */}
      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={() => setDraft(null)}
              data-testid="abi-dashboard-prepare-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold"
                      style={{ color: "#7A5B14" }}>Brouillon préparé</div>
                <h3 className="font-serif text-xl font-bold text-slate-900"
                    style={{ color: "#0B2545" }}>{draft.action.title_fr}</h3>
              </div>
              <button className="text-slate-500 hover:text-slate-900"
                       onClick={() => setDraft(null)}>✕</button>
            </div>
            <div className="flex items-center gap-2 mb-3">
              {["email", "call_script", "linkedin_message", "questions"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={async () => {
                    setDraftLoading(true);
                    try {
                      const r = await api.post("/abi/copilot/prepare", {
                        opportunity_id: draft.action.opportunity_id, kind: k,
                      });
                      setDraft({ ...draft, kind: k, text: r.data?.draft_fr,
                                  disclaimer: r.data?.disclaimer_fr });
                    } catch { toast.error("Erreur"); }
                    finally { setDraftLoading(false); }
                  }}
                  className={`text-xs px-3 py-1 rounded-full border ${draft.kind === k
                    ? "text-white" : "text-slate-700 bg-white"}`}
                  style={draft.kind === k
                    ? { background: "#0B2545", borderColor: "#0B2545" }
                    : { borderColor: "#E5E7EB" }}
                  data-testid={`abi-dashboard-prepare-kind-${k}`}
                >
                  {k === "email" ? "Courriel"
                    : k === "call_script" ? "Script d'appel"
                    : k === "linkedin_message" ? "LinkedIn"
                    : "Questions"}
                </button>
              ))}
            </div>
            {draftLoading ? (
              <div className="p-8 flex items-center gap-2 text-slate-600">
                <Loader2 size={16} className="animate-spin" /> Génération en cours…
              </div>
            ) : (
              <>
                <textarea
                  value={draft.text || ""}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  className="w-full min-h-[280px] p-3 border rounded-lg text-sm font-mono"
                  style={{ borderColor: "#E5E7EB" }}
                  data-testid="abi-dashboard-prepare-textarea"
                />
                <p className="text-xs italic text-slate-500 mt-2">
                  {draft.disclaimer || "Relire, personnaliser et valider avant tout envoi."}
                </p>
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(draft.text || ""); toast.success("Copié dans le presse-papier."); }}
                    className="text-xs px-4 py-2 rounded-lg border hover:bg-slate-50"
                    style={{ borderColor: "#E5E7EB" }}
                    data-testid="abi-dashboard-prepare-copy"
                  >
                    Copier
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft(null)}
                    className="text-xs px-4 py-2 rounded-lg font-semibold text-white"
                    style={{ background: "#0B2545" }}
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
